<template>
  <div id="work-in-progress-page" v-if="!loading">
    <div class="flex-grow my-4">
      <h1>{{ $t('WorkInProgress') }}</h1>
    </div>

      <vs-tabs>
        <vs-tab :label="$t('FirmOrderAndReferrals')">
          <div class="vx-card full-page">
            <grid-panel
                :column-defs="firmOrdersColumnDefs"
                :url="firmOrdersUrl"
                :apiParams="apiParams"
                :gridOptions="gridOptionsFirmOrders"
                :defaultColDef="defaultColDef"
                :headerTextWrap="true"
                @apiAction="apiAction"
            >
              <template slot="right">
                <date-range-picker class="ml-4" @update:startDate="startDate = $event" @update:endDate="endDate = $event" />
              </template>
            </grid-panel>
          </div>
        </vs-tab>
        <vs-tab :label="$t('Referrals')">
          <div class="vx-card">
            <grid-panel
                :column-defs="referralsColumnDefs"
                :url="referralsUrl"
                :apiParams="apiParams"
                :gridOptions="gridOptionsReferrals"
                :defaultColDef="defaultColDef"
                :headerTextWrap="true"
            >
              <template slot="right">
                <date-range-picker class="ml-4" @update:startDate="startDate = $event" @update:endDate="endDate = $event" />
              </template>
            </grid-panel>
          </div>
        </vs-tab>
        <vs-tab :label="$t('SavedQuotes')">
          <div class="vx-card">
            <grid-panel
                :column-defs="savedQuotesColumnDefs"
                :url="savedQuotesUrl"
                :apiParams="apiParams"
                :gridOptions="gridOptionsSavedQuotes"
                :defaultColDef="defaultColDef"
                :headerTextWrap="true"
            >
              <template slot="right">
                <date-range-picker class="ml-4" @update:startDate="startDate = $event" @update:endDate="endDate = $event" />
              </template>
            </grid-panel>
          </div>
        </vs-tab>
        <vs-tab :label="$t('RequestNotProperlyReceived')">
          <div class="vx-card">
            <grid-panel
                :column-defs="notReceivedColumnDefs"
                :url="notReceivedUrl"
                :apiParams="apiParams"
                :gridOptions="gridOptionsNotReceived"
                :defaultColDef="defaultColDef"
                :headerTextWrap="true"
            >
              <template slot="right">
                <date-range-picker class="ml-4" @update:startDate="startDate = $event" @update:endDate="endDate = $event" />
              </template>
            </grid-panel>
          </div>
        </vs-tab>
      </vs-tabs>

      <vs-popup
        :title="$t('Confirm')"
        id="api-action-confirmation"
        :active.sync="apiActionConfirmationPopup"
      >
        <p>{{ apiActionConfirmationPopupMessage }}</p>
        <div class="mt-4 flex flex-wrap items-center justify-end">
          <vs-button @click="apiActionConfirm" color="secondary" type="filled">{{ $t('Confirm') }}</vs-button>
        </div>
      </vs-popup>

      <vs-popup
        title=""
        id="api-action"
        :active.sync="apiActionPopup"
      >
        <p>{{ apiActionPopupMessage }}</p>
      </vs-popup>
    </div>
</template>

<script>
  import GridPanel from '@/views/components/ag-grid/GridPanel';
  import GridActionsMenu from "../../components/extra-components/GridActionsMenu";
  import FirmOrdersConfig from "../../../grids/WorkInProgress/FirmOrdersConfig";
  import SavedQuotesConfig from "../../../grids/WorkInProgress/SavedQuotesConfig";
  import SavedReferralsConfig from "../../../grids/WorkInProgress/SavedReferralsConfig";
  import NotReceivedConfig from "../../../grids/WorkInProgress/NotReceivedConfig";
  import DateRangePicker from "../../components/calendar/DateRangePicker";
  import CellRendererDate from "../../components/ag-grid/cell-renderer/CellRendererDate";
  import CellRendererAction from "../../components/ag-grid/cell-renderer/CellRendererAction";
  import CellRendererCurrency from "../../components/ag-grid/cell-renderer/CellRendererCurrency";
  import CellRendererButton from '@/views/components/ag-grid/cell-renderer/CellRendererButton';
  import CellRendererApiButton from '@/views/components/ag-grid/cell-renderer/CellRendererApiButton';
  import SetGridOptionsMixin from "@/components/mixins/SetGridOptionsMixin";
  import ToolTipRenderer from '@/views/components/ag-grid/components/ToolTipRenderer';
  import CellRendererMasterValue from "../../components/ag-grid/cell-renderer/CellRendererMasterValue";
  import {
    TABLE_IDENTIFIER_FW_ORDER_ID,
    TABLE_IDENTIFIER_FW_SUBMISSION_ID,
    TABLE_IDENTIFIER_FW_SAVED_QUOTES_ID,
    TABLE_IDENTIFIER_FW_REFERRALS_ID,
  } from '@/constants'
  import moment from 'moment';

  export default {
    components: {
      GridPanel,
      GridActionsMenu,
      DateRangePicker,
      CellRendererDate,
      CellRendererAction,
      CellRendererCurrency,
      ToolTipRenderer,
      CellRendererButton,
      CellRendererMasterValue,
      CellRendererApiButton,
    },
    mixins: [SetGridOptionsMixin],
    data() {
      return {
        apiActionConfirmationPopup: false,
        apiActionConfirmationPopupMessage: null,
        apiActionPopup: false,
        apiActionPopupMessage: null,
        apiActionItemId: null,
        firmOrdersUrl: (params) => this.$api.workInProgress.list(params),
        notReceivedUrl: (params) => this.$api.orderSubmission.list(params),
        savedQuotesUrl: (params) => this.$api.savedQuote.list(params),
        referralsUrl: (params) => this.$api.referral.list(params),
        startDate: null,
        endDate: null,
        gridOptionsFirmOrders: {},
        gridOptionsNotReceived: {},
        gridOptionsSavedQuotes: {},
        gridOptionsReferrals: {},
        defaultColDef: {
          minWidth: 100,
          resizable: true,
          sortable: true,
          wrapText: true,
          tooltipComponent: 'ToolTipRenderer',
        },
        loading: true,
        firmOrdersWidthsConfig: null,
        notReceivedWidthsConfig: null,
        firmOrdersColumnDefs: [],
        notReceivedColumnDefs: [],
        savedQuotesColumnDefs: [],
        referralsColumnDefs: [],
        languageApiParams: { language: this.$i18n.locale },
      };
    },
    computed: {
      apiParams() {
        // Only set these when we have the full date range available
        // Otherwise it loads the endpoint twice and fails the first time due to missing parameters
        if (!this.startDate || !this.endDate) {
          return null;
        }

        return {
          ...this.languageApiParams,
          startDate: (this.startDate) ? moment(this.startDate).format('YYYY-MM-DD') : null,
          endDate: (this.endDate) ? moment(this.endDate).format('YYYY-MM-DD') : null
        }
      },
    },
    methods: {
      apiAction(params) {
        if (params.confirmAction) {
          this.apiActionConfirmationPopupMessage = params.confirmActionMessage;
          this.apiActionItemId = params.itemId;
          this.apiActionConfirmationPopup = true;
        }
      },
      async apiActionConfirm() {
        this.apiActionConfirmationPopup = false;
        this.apiActionPopupMessage = null;
        const response = await this.$api.workInProgress.firmOrderAction(this.apiActionItemId, this.$i18n.locale)

        if (!response.data.data.Success) {
          this.$vs.notify({
              title: 'Error',
              text: response.data.data.Message,
              iconPack: 'feather',
              icon: 'icon-alert-circle',
              color: 'danger',
              position: 'top-right'
            });
        } else if (response.data.data.IsURL) {
          window.open(response.data.data.ReturningDataString, '_blank');
        } else {
          this.apiActionPopup = true;
          this.apiActionPopupMessage = response.data.data.ReturningDataString;
        }

        this.apiActionItemId = null;
      },
      async setColumnDefs() {
        this.loading = true;
        
        const firmOrdersWidthsConfig = await this.setGridWidthOptions(TABLE_IDENTIFIER_FW_ORDER_ID);
        this.setFirmOrdersColumnDefs(firmOrdersWidthsConfig);

        const notReceivedWidthsConfig = await this.setGridWidthOptions(TABLE_IDENTIFIER_FW_SUBMISSION_ID);
        this.setNotReceivedColumnDefs(notReceivedWidthsConfig);

        const savedQuotesWidthsConfig = await this.setGridWidthOptions(TABLE_IDENTIFIER_FW_SAVED_QUOTES_ID);
        this.setSavedQuotesColumnDefs(savedQuotesWidthsConfig);
        
        const savedReferralsWidthsConfig = await this.setGridWidthOptions(TABLE_IDENTIFIER_FW_REFERRALS_ID);
        this.setReferralsColumnDefs(savedReferralsWidthsConfig);

        this.loading = false;
      },
      setFirmOrdersColumnDefs(firmOrdersWidthsConfig) {
        this.firmOrdersColumnDefs = this.getColumnDefsWithWidths(FirmOrdersConfig, firmOrdersWidthsConfig);
      },
      setNotReceivedColumnDefs(notReceivedWidthsConfig) {
        this.notReceivedColumnDefs = this.getColumnDefsWithWidths(NotReceivedConfig, notReceivedWidthsConfig);
      },
      setSavedQuotesColumnDefs(savedQuotesWidthsConfig) {
        this.savedQuotesColumnDefs = this.getColumnDefsWithWidths(SavedQuotesConfig, savedQuotesWidthsConfig);
      },
      setReferralsColumnDefs(savedQuotesWidthsConfig) {
        this.referralsColumnDefs = this.getColumnDefsWithWidths(SavedReferralsConfig, savedQuotesWidthsConfig);
      },
    },
    async created() {
      await this.setColumnDefs();
    },
  };
</script>
